import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { fromJst } from "@/utils/time";
import { TopScrollIcon } from "@roman/shared/components/Icons";
import { DEFAULT_PER_PAGE } from "@roman/shared/constants";
import {
  type LeaderboardCycleWithSeason,
  useGetAllLeaderboardCycle,
} from "@roman/shared/hooks/leaderboard/useGetAllLeaderboardCycle";
import { useGetLeaderboardRankById } from "@roman/shared/hooks/leaderboard/useGetLeaderboardRankById";
import { useGetMyLeaderboardRank } from "@roman/shared/hooks/leaderboard/useGetMyLeaderboardRank";
import { useGetRankingDistribution } from "@roman/shared/hooks/leaderboard/useGetRankingDistribution";
import { useIsMobile } from "@roman/shared/hooks/soccer/useIsMobile";
import { useInfiniteScroll } from "@roman/shared/hooks/useInfiniteScroll";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { RankedUser } from "@roman/shared/types/WebUser";
import { cn } from "@roman/shared/utils";
import { addDays, setHours } from "date-fns";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { LeaderboardInfoCard, LeaderboardInfoCardLoadingSkeleton } from "./components/LeaderboardInfoCard";
import { RankingRewardsModal } from "./components/RankingRewardsModal";
import { SeasonRangeTime } from "./components/SeasonRangeTime";
import { SelectCycles } from "./components/SelectCycles";
import { UserRow } from "./components/UserRow";
import { UserTop3Card } from "./components/UserTop3Card";
import { WarningModal } from "./components/WarningModal";

const DEFAULT_START_DATE = fromJst("2025-07-03T12:00:00");
const LEADERBOARD_PERIOD_DAYS = 14;

const calculateDates = (startDate?: Date, endDate?: Date) => {
  // For ended seasons after Season 1
  if (startDate && endDate) {
    let start = new Date(startDate);
    let end = new Date(endDate);
    start.setHours(12, 0, 0, 0);
    end.setHours(12, 0, 0, 0);

    start = fromJst(start);
    end = fromJst(end);

    return {
      startDate: new Date(start),
      endDate: new Date(end),
    };
  }

  // For ended Season 1: which has no start date but has an end date
  if (!startDate && endDate) {
    let end = new Date(endDate);
    end.setHours(12, 0, 0, 0);
    end = fromJst(end);
    const start = addDays(end, -LEADERBOARD_PERIOD_DAYS);

    return { startDate: start, endDate: end };
  }

  // For ongoing seasons after Season 1: which has a start date but no end date
  if (startDate && !endDate) {
    let start = new Date(startDate);
    start.setHours(12, 0, 0, 0);
    start = fromJst(start);

    const end = addDays(start, LEADERBOARD_PERIOD_DAYS);
    return { startDate: start, endDate: end };
  }

  // For Season 1: which will have no start or end date
  const defaultStart = DEFAULT_START_DATE;
  let defaultEnd = addDays(defaultStart, LEADERBOARD_PERIOD_DAYS);
  defaultEnd = setHours(defaultEnd, 12);
  defaultEnd = fromJst(defaultEnd);

  return { startDate: defaultStart, endDate: defaultEnd };
};

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const [leaderboardCycle, setLeaderboardCycle] = useState<LeaderboardCycleWithSeason>();
  const [leaderboardCycles, setLeaderboardCycles] = useState<LeaderboardCycleWithSeason[]>();
  const [isViewRankingRewards, setIsViewRankingRewards] = useState(false);
  const [isViewWarningModal, setIsViewWarningModal] = useState(false);
  const isUnAuthenticated = useAuthStore((state) => state.authState === "unauthenticated");

  const isMobile = useIsMobile();

  const { users, page, perPage, triggerAtPercentage, isLoading, triggerRef, setPage } = useInfiniteScroll(
    useGetLeaderboardRankById,
    {
      leaderboardCycleId: leaderboardCycle?.id,
      swrOptions: {
        onError: useHandleError("リーダーボードの取得に失敗しました"),
      },
    },
  );

  const { myRank } = useGetMyLeaderboardRank({
    leaderboardCycleId: leaderboardCycle?.id,
    swrOptions: {
      onError: useHandleError("自分の順位の取得に失敗しました"),
    },
  });

  const { rankingRewardSlices, isLoading: isLoadingRankingDistribution } = useGetRankingDistribution({
    leaderboardCycleId: leaderboardCycle?.id,
    swrOptions: {
      onError: useHandleError("ランキング分布の取得に失敗しました"),
    },
  });

  const { leaderboardCycles: leaderboardCyclesData, isLoading: isLeaderboardCycleLoading } = useGetAllLeaderboardCycle({
    swrOptions: {
      onError: useHandleError("月間ランキングの取得に失敗しました"),
    },
  });

  const { inView } = useInView({ threshold: 0, triggerOnce: false });
  const { ref: myRankRef, inView: isMyRankInView } = useInView({
    threshold: 1,
    rootMargin: "0px 0px -70px 0px",
  });

  // --- Scroll-to-top button logic ---
  const [isFirstUserOutOfView, setIsFirstUserOutOfView] = useState(false);
  const { ref: firstUserRef, inView: isFirstUserInView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (currentUser?.freePoints === "0" || isUnAuthenticated) {
      setIsViewWarningModal(true);
    } else {
      setIsViewWarningModal(false);
    }
  }, [isUnAuthenticated, currentUser]);

  useEffect(() => {
    setIsFirstUserOutOfView(!isFirstUserInView);
  }, [isFirstUserInView]);

  useEffect(() => {
    if (inView) setPage((prevPage) => prevPage + 1);
  }, [inView, setPage]);

  useEffect(() => {
    if (leaderboardCyclesData && leaderboardCyclesData.length > 0) {
      const updatedCycles = leaderboardCyclesData.map((cycle) => {
        const { startDate, endDate } = calculateDates(
          cycle.startDate ? new Date(cycle.startDate) : undefined,
          cycle.endDate ? new Date(cycle.endDate) : undefined,
        );
        return {
          ...cycle,
          startDate: startDate ?? null,
          endDate: endDate ?? null,
        };
      });

      setLeaderboardCycles(updatedCycles);

      const activeCycle = updatedCycles.find((cycle) => cycle.concludedAt == null);
      setLeaderboardCycle(activeCycle);
    }
  }, [leaderboardCyclesData]);

  const top3Users = users.slice(0, 3);
  const remainingUsers = users.slice(3);
  const isShowMyRankInBottom = myRank && myRank.rank > 3 && !isMyRankInView;

  if (isLoading || isLeaderboardCycleLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <div className="min-h-screen bg-white">
        {!isLoading && !isLeaderboardCycleLoading && leaderboardCycle && (
          <LeaderboardInfoCard
            leaderboardCycle={leaderboardCycle}
            currentRank={myRank?.rank}
            freePointsToNextRank={myRank?.freePointsToNextRank}
            potentialRedeemablePoints={myRank?.potentialRedeemablePoints}
          />
        )}

        <div className="px-2 h-[50px] bg-white flex items-center justify-between">
          <div className="flex items-center justify-center">
            <span className="text-[20px] font-extrabold tracking-[-0.4px] text-black">{leaderboardCycle?.name}</span>
            <span className="text-black text-[14px] font-medium tracking-[-0.28px] ml-2 pt-[2px]">
              {leaderboardCycle && (
                <SeasonRangeTime startDate={leaderboardCycle.startDate} endDate={leaderboardCycle.endDate} />
              )}
            </span>
          </div>

          <SelectCycles
            selectedLeaderCycle={leaderboardCycle}
            setSelectedLeaderCycle={setLeaderboardCycle}
            leaderboardCycles={leaderboardCycles ?? undefined}
          />
        </div>

        <div className={cn("relative", (isLoading || isLeaderboardCycleLoading || !leaderboardCycle) && "opacity-0")}>
          <div className="w-full sticky top-[72px] z-10 grid grid-cols-3 gap-0.5 bg-white px-2 pb-0">
            {top3Users.map((user, index) => (
              <UserTop3Card key={user.id} user={user} index={index} isMyRank={user.id === currentUser?.id} />
            ))}
          </div>
          <div
            onClick={() => {
              setIsViewRankingRewards(true);
            }}
            className="bg-orange mx-[10px] mt-[10px] py-[3px] rounded-[4px] h-[26px] mb-[10px] flex justify-center items-center gap-[2px]"
          >
            <div className="text-white text-center font-bold text-[12px]">ランキング報酬について</div>
            <div className="rounded-full w-5 h-5 border-orange border-2 flex justify-center items-center bg-white">
              <div className="text-center font-bold text-[14px] tracking-[-0.14px] text-orange mx-[7.37px]">i</div>
            </div>
          </div>
          <div className="min-h-screen">
            {remainingUsers.map((user: RankedUser, index: number) => {
              const startRank = index + 4;
              const fetchPoint = users.length - Math.floor(perPage * triggerAtPercentage);
              const isReachedFetchPoint = startRank === fetchPoint;
              // Attach firstUserRef to the first remaining user
              const rowRef = index === 1 ? firstUserRef : undefined;
              return (
                <div key={user.id}>
                  <UserRow
                    user={user}
                    rank={user.rank}
                    className={user.id === currentUser?.id ? "bg-blue" : undefined}
                    ref={user.id === currentUser?.id ? myRankRef : rowRef}
                  />
                  {!isLoading && isReachedFetchPoint && <div ref={triggerRef} />}
                </div>
              );
            })}

            {(!users.length || isLoading) && page === 1 && (
              <div className="flex flex-col gap-3">
                {Array.from({ length: DEFAULT_PER_PAGE }).map((_, index) => (
                  <Skeleton className="w-full h-10" key={index} />
                ))}
              </div>
            )}
          </div>

          <div className={`sticky ${isMobile ? "bottom-[70px]" : "bottom-[0px]"} w-full bg-white`}>
            {currentUser && isShowMyRankInBottom && (
              <UserRow user={{ ...currentUser, ...myRank }} rank={myRank.rank} className="bg-blue" />
            )}

            <div className="px-3 py-2.5 relative">
              {myRank && Number(myRank.freePointsToNextRank) > 0 && !leaderboardCycle?.concludedAt && (
                <span className="font-bold text-[13px] text-orange absolute -top-[14px] left-1/2 -translate-x-1/2 bg-white py-[5px] px-[9px] rounded-full shadow whitespace-nowrap">
                  次の順位まで：
                  {Number(myRank.freePointsToNextRank).toLocaleString()} pt
                </span>
              )}

              <Button
                variant="default"
                className="w-full h-[50px] bg-green text-white font-bold rounded-lg text-lg hover:bg-green/90"
                onClick={() => navigate("/redeem-points")}
              >
                ポイントを獲得する
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll-to-top button */}
        {isFirstUserOutOfView && (
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`sticky left-[100%] mr-3 z-50 bg-primary text-white p-3 shadow-lg transition w-[51px] h-[51px] rounded-full border border-white opacity-90
              ${
                currentUser && isShowMyRankInBottom
                  ? isMobile
                    ? "bottom-[220px]"
                    : "bottom-[150px]"
                  : isMobile
                    ? "bottom-[160px]"
                    : "bottom-[90px]"
              }
            `}
            aria-label="ページの先頭に戻る"
          >
            <div className="flex items-center justify-center flex-col">
              <TopScrollIcon />
              <div className="text-center font-noto-sans-jp text-[10px] font-bold">TOP</div>
            </div>
          </button>
        )}
      </div>

      {leaderboardCycle && (
        <RankingRewardsModal
          rankingRewardSlices={rankingRewardSlices}
          isLoading={isLoadingRankingDistribution}
          isViewRankingRewards={isViewRankingRewards}
          setIsViewRankingRewards={setIsViewRankingRewards}
        />
      )}

      {isViewWarningModal && (
        <WarningModal
          freePoints={currentUser?.freePoints}
          isViewWarningModal={isViewWarningModal}
          setIsViewWarningModal={setIsViewWarningModal}
        />
      )}
    </>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="bg-white px-2">
      <LeaderboardInfoCardLoadingSkeleton />

      <Skeleton className="h-10 w-ful my-2" />

      <div className="flex items-center justify-center gap-1">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            className="w-1/3 flex flex-col justify-center items-center rounded-ee-lg rounded-ss-lg p-2 relative"
          >
            <Skeleton className="h-[30px] w-[30px] rounded-full mb-1" />

            <Skeleton className="h-3 w-14 mb-0.5" />

            <Skeleton className="h-4 w-24 mb-1.5" />

            <Skeleton className="h-5 w-14 rounded-full" />

            <div className="absolute top-2 left-2">
              <Skeleton className="h-8 w-6" />
            </div>
          </Skeleton>
        ))}
      </div>

      <Skeleton className="h-6 w-full rounded-lg my-2" />

      {Array.from({ length: 20 }).map((_, index) => (
        <div key={index} className="flex items-center justify-between py-1">
          <Skeleton className="h-12 w-full" />
        </div>
      ))}
    </div>
  );
};

export default Leaderboard;
